import React, { useEffect, useState } from "react";
import { Box, Popover, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { DateRange as DateRangeIcon, Payments, AccountBalanceWallet, Close } from "@mui/icons-material";
import { purple, green } from "@mui/material/colors";
import { DatePicker } from "@mui/x-date-pickers";
import { endOfMonth, format, startOfMonth } from "date-fns";
import ButtonComponent from "../../../Components/ButtonComponent";
import MaterialTable from "../../../Components/MaterialTable";
import AffiliateSalesCardsList from "./AffiliateSalesCardsList";
import { IColumn } from "../../../Components/MaterialTable/MaterialTable";
import { formatCurrency } from "../../../utils/formatCurrency";
import { formatDate } from "../../../utils/formatDate";
import { AffiliateSale } from "../../../store/services/affiliate/dto/affiliate.dto";
import { useLazyGetAffiliateSalesQuery } from "../../../store/services/affiliate/affiliate.service";
import { StatWrapper } from "./styles";
import { useAppSelector } from "../../../store";
import { getFullAffiliateState } from "../../../store/slices/affiliate/affiliate.slice";

const columns: IColumn<AffiliateSale>[] = [
	{
		label: "Transaction id",
		dataKey: "transactionId",
		Cell: ({ rowData }) => <Typography fontSize={13}>{rowData.transactionId}</Typography>
	},
	{
		label: "Invitation id",
		dataKey: "invitationId",
		Cell: ({ rowData }) => <Typography fontSize={13}>{rowData.invitationId}</Typography>
	},
	{
		label: "Buyer",
		dataKey: "buyerName",
		Cell: ({ rowData }) => <Typography fontSize={13}>{rowData.user.firstName + " " + rowData.user.lastName}</Typography>
	},
	{
		label: "Sale Amount",
		dataKey: "saleAmount",
		Cell: ({ rowData }) => (
			<Typography fontSize={13} fontWeight={500}>
				{formatCurrency(rowData.amount, rowData.currency)}
			</Typography>
		)
	},
	{
		label: "Date",
		dataKey: "createdAt",
		Cell: ({ rowData }) => <Typography fontSize={13}>{formatDate(rowData.createdAt, "Africa/Cairo", "PP")}</Typography>
	}
];
const PER_PAGE = 20;
const AffiliateSalesHistory = () => {
	const theme = useTheme();
	const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));
	const [startDate, setStartDate] = useState<Date | null>(startOfMonth(new Date()));
	const [endDate, setEndDate] = useState<Date | null>(endOfMonth(new Date()));
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [page, setPage] = useState(1);
	const {
		affiliate,
		affiliateSales: sales,
		isFetchingAffiliateSales: isLoading,
		affiliateSalesTotalItems,
		affiliateSalesRangeTotalSales,
		affiliateSalesRangeTotalCommission
	} = useAppSelector(getFullAffiliateState);
	const [FetchAffiliateSales] = useLazyGetAffiliateSalesQuery();

	const hasFilter = !!startDate && !!endDate;

	useEffect(() => {
		if (affiliate && startDate && endDate) {
			setPage(1);
			FetchAffiliateSales({
				page: 1,
				perPage: PER_PAGE,
				from: format(startDate, "yyyy-MM-dd"),
				to: format(endDate, "yyyy-MM-dd")
			});
		}
	}, [affiliate, startDate, endDate]);

	const totalSales = affiliateSalesRangeTotalSales ?? 0;
	const netSales = affiliateSalesRangeTotalCommission ?? 0;

	const rangeLabel =
		startDate && endDate ? `${format(startDate, "PP")} - ${format(endDate, "PP")}` : "Select date range";

	const handleClear = () => {
		setStartDate(startOfMonth(new Date()));
		setEndDate(endOfMonth(new Date()));
		setAnchorEl(null);
	};

	const handlePageChange = (newPage: number) => {
		if (newPage > 0) {
			setPage(newPage);
			const body = { page: newPage, perPage: PER_PAGE } as {
				page: number;
				perPage: number;
				from: string;
				to: string;
			};
			if (startDate && endDate) {
				body["from"] = format(startDate, "yyyy-MM-dd");
				body["to"] = format(endDate, "yyyy-MM-dd");
			}
			FetchAffiliateSales(body);
		}
	};

	return (
		<Box>
			<Box display={"flex"} justifyContent={"flex-end"} alignItems={"center"} gap={1} flexWrap={"wrap"} mb={3}>
				<ButtonComponent
					variant={"outlined"}
					color={"inherit"}
					loading={false}
					startIcon={<DateRangeIcon />}
					onClick={e => setAnchorEl(e.currentTarget)}
					text={rangeLabel}
				/>
				{hasFilter && (
					<ButtonComponent
						variant={"text"}
						color={"inherit"}
						loading={false}
						startIcon={<Close />}
						onClick={handleClear}
						text={"Clear Filter"}
					/>
				)}
				<Popover
					open={!!anchorEl}
					anchorEl={anchorEl}
					onClose={() => setAnchorEl(null)}
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					transformOrigin={{ vertical: "top", horizontal: "right" }}
				>
					<Box display={"flex"} flexDirection={isSmallerScreen ? "column" : "row"} gap={2} p={2}>
						<DatePicker
							label={"From"}
							value={startDate}
							onChange={newValue => setStartDate(newValue)}
							maxDate={endDate ?? undefined}
							slotProps={{ textField: { size: "small" } }}
						/>
						<DatePicker
							label={"To"}
							value={endDate}
							onChange={newValue => setEndDate(newValue)}
							minDate={startDate ?? undefined}
							slotProps={{ textField: { size: "small" } }}
						/>
					</Box>
				</Popover>
			</Box>

			<Box display={"flex"} flexWrap={"wrap"} gap={2} mb={3}>
				<StatWrapper $statColor={purple[500]}>
					<Box className={"icon"}>
						<Payments />
					</Box>
					<Box className={"stats"}>
						<Typography fontWeight={800} variant={"h5"} lineHeight={1.1}>
							{formatCurrency(totalSales, "EGP")}
						</Typography>
						<Typography fontWeight={600} fontSize={13} mt={0.3}>
							Total Sales
						</Typography>
					</Box>
				</StatWrapper>
				<StatWrapper $statColor={green[600]}>
					<Box className={"icon"}>
						<AccountBalanceWallet />
					</Box>
					<Box className={"stats"}>
						<Typography fontWeight={800} variant={"h5"} lineHeight={1.1}>
							{formatCurrency(netSales, "EGP")}
						</Typography>
						<Typography fontWeight={600} fontSize={13} mt={0.3}>
							Net Sales
						</Typography>
					</Box>
				</StatWrapper>
			</Box>

			{isSmallerScreen ? (
				isLoading ? (
					<Skeleton variant={"rectangular"} height={200} />
				) : (
					<AffiliateSalesCardsList
						data={sales}
						page={page}
						perPage={PER_PAGE}
						totalItems={affiliateSalesTotalItems}
						onPageChange={handlePageChange}
					/>
				)
			) : (
				<MaterialTable
					tableLabel={"Sales History"}
					columns={columns}
					data={sales}
					isLoading={isLoading}
					page={page}
					rowsPerPage={PER_PAGE}
					totalItems={affiliateSalesTotalItems}
					onPageChange={handlePageChange}
				/>
			)}
		</Box>
	);
};

export default AffiliateSalesHistory;
